import * as React from 'react'

import apis from '@app/api/api'

import Images from '@components/images/images'
import Button from '@components/button/button'

import useI18n from '@store/i18n/useI18n'

import Theme from '@theme/Theme'
import styled from '@theme/styled-components'

import { motion, item } from '@utils/Motion'
import DownloadingComponent from '@utils/ImageDownloader'

import Loader from 'react-loader-spinner'
import Swal from 'sweetalert2'

interface Props {
  screenStatus: ScreenStatus
  declaration?: BusinessDeclaration
  pictureName: string
  business?: BusinessDetails
}

const DeclarationDetailsContent = ({ screenStatus, declaration, pictureName, business }: Props) => {
  const i18n = useI18n()
  const [downloadPictures, setDownloadPictures] = React.useState<boolean>(false)
  const [downloading, setDownloading] = React.useState<boolean>(false)
  const [picturesToDownload, setPicturesToDownload] = React.useState<BusinessDeclarationPicture[]>([])
  const [pictures, setPictures] = React.useState<BusinessDeclarationPicture[]>()

  React.useEffect(() => {
    if (declaration) {
      setPictures(declaration.pictures)
    }
  }, [declaration?.pictures])

  const onPressPicture = (picture: BusinessDeclarationPicture) =>
    picturesToDownload.includes(picture)
      ? setPicturesToDownload(picturesToDownload.filter((pic) => pic.id !== picture.id))
      : setPicturesToDownload([...picturesToDownload, picture])

  const deletePicture = (imageId: string, declaration: Declaration) => {
    if (declaration) {
      const newDeclaration: Declaration = {
        ...declaration,
        pictures: declaration?.pictures.filter((oldPicture) => oldPicture.id !== imageId) ?? [],
      }

      if (newDeclaration) {
        apis.declaration
          .edit(newDeclaration)
          .then((res) => {
            setPictures(pictures ? [...pictures.filter((d) => d.id !== imageId)] : [])
            Swal.fire({
              title: i18n.t(`components.detailsChantier.alertDeleting.success.title`),
              text: i18n.t(`components.detailsChantier.alertDeleting.success.text`),
              icon: 'success',
            })
          })
          .catch(() => {
            Swal.fire({
              title: i18n.t(`components.detailsChantier.alertDeleting.failure.title`),
              text: i18n.t(`components.detailsChantier.alertDeleting.failure.text`),
              icon: 'error',
            })
          })
      }
    }
  }

  const confirmDeletePhoto = (imageId: string) => {
    if (business) {
      const oldDeclaration = business.declarations.find((d) => {
        return d.pictures.find((p) => p.id === imageId)
      })
      if (oldDeclaration) {
        return oldDeclaration?.breakpoint
          ? Swal.fire({
              title: i18n.t(`components.detailsChantier.alertDeleting.breakpoint.title`),
              text: i18n.t(`components.detailsChantier.alertDeleting.breakpoint.text`),
              icon: 'error',
            })
          : Swal.fire({
              title: i18n.t('components.detailsChantier.alertDeleting.title'),
              text: i18n.t('components.detailsChantier.alertDeleting.text'),
              showCancelButton: true,
              confirmButtonText: i18n.t('components.adminCompanies.alertCancelling.confirm'),
              confirmButtonColor: 'green',
              cancelButtonText: i18n.t('components.adminCompanies.alertCancelling.cancel'),
              cancelButtonColor: 'red',
            }).then((result) => {
              if (result.isConfirmed) {
                Swal.fire({
                  didOpen: () => {
                    Swal.showLoading()
                  },
                })
                deletePicture(imageId, oldDeclaration)
              }
            })
      }
    }
  }

  return (
    <Container loading={screenStatus === 'loading'}>
      {screenStatus === 'loading' ? (
        <StatusContainer>
          <Loader type="ThreeDots" color={Theme.colors.classicButtonLoader} height={30} />
        </StatusContainer>
      ) : screenStatus === 'error' || !declaration ? (
        <ErrorContainer>
          <ErrorLibelle>{i18n.t('components.detailsChantier.error')}</ErrorLibelle>
        </ErrorContainer>
      ) : (
        <AllImagesContainer>
          <ButtonsContainer>
            <ActionContainer hide={false}>
              <Button
                title={i18n.t(
                  `components.businessDetails.buttons.${
                    downloadPictures
                      ? picturesToDownload.length !== 0
                        ? 'downloadCountPictures'
                        : 'selectPictures'
                      : 'downloadPictures'
                  }`,
                  {
                    count: picturesToDownload.length,
                  }
                )}
                onClick={() => {
                  if (!downloadPictures) {
                    setDownloadPictures(true)
                  } else {
                    setDownloading(true)
                  }
                }}
                type={downloadPictures ? 'navigation' : 'classic'}
                status={
                  downloadPictures && picturesToDownload.length === 0 ? 'disabled' : downloading ? 'loading' : 'active'
                }
                size="medium"
                icon="download"
              />
            </ActionContainer>
            {downloadPictures && (
              <Button
                title={i18n.t(`components.businessDetails.buttons.cancel`)}
                onClick={() => {
                  setDownloadPictures(false)
                  setPicturesToDownload([])
                }}
                type="classic"
                status="active"
                size="medium"
                icon="cancel"
              />
            )}
          </ButtonsContainer>
          <ImagesContainer as={motion.div} className="item" variants={item} initial="hidden" animate="visible">
            {!pictures || pictures?.length === 0 ? (
              <NoPhotoContainer>
                <NoPhotoLibelle>{i18n.t('components.detailsChantier.noPhoto')}</NoPhotoLibelle>
              </NoPhotoContainer>
            ) : (
              pictures?.map((picture, index) => (
                <Images
                  key={picture.id}
                  imageId={picture.id}
                  index={index}
                  images={declaration.pictures}
                  size={250}
                  fromdetailbusiness
                  removingPictures={downloadPictures}
                  setSelectedPicture={() => onPressPicture(picture)}
                  selected={picturesToDownload.includes(picture)}
                  status={picture.status}
                  deletePicture={downloadPictures ? undefined : confirmDeletePhoto}
                />
              ))
            )}
          </ImagesContainer>
        </AllImagesContainer>
      )}
      {downloading && (
        <DownloadingComponent
          pictures={picturesToDownload.map((p) => ({
            id: p.id,
            path: '',
            name: pictureName,
          }))}
          cancel={() => {
            setDownloading(false)
            setPicturesToDownload([])
            setDownloadPictures(false)
          }}
        />
      )}
    </Container>
  )
}
export default DeclarationDetailsContent

const Container = styled('div')<{ loading: boolean }>`
  flex: 1;
  overflow: hidden;
  overflow-y: scroll;
  ${(props) => (props.loading ? 'padding-bottom: 0px; padding-top: 53px;' : 'padding-bottom: 60px; padding-top: 10px;')}
  background-color: ${(props) => props.theme.colors.filterBackgroundColor};
`

const StatusContainer = styled('div')`
  display: flex;
  flex: 1;
  align-items: center;
  justify-content: center;
  height: 100%;
`
const ErrorContainer = styled('div')`
  height: 100%;
  display: flex;
  margin-top: 56px;
  align-items: center;
  justify-content: center;
`
const ErrorLibelle = styled('div')`
  ${(props) => props.theme.fonts.noItem};
  text-align: center;
  margin: 40px;
`
const ImagesContainer = styled('div')`
  overflow: scroll;
  display: flex;
  flex: 1;
  flex-wrap: wrap;
  padding: 25px;
  flex-direction: row;
`
const NoPhotoContainer = styled('div')`
  flex: 1;
`
const NoPhotoLibelle = styled('div')`
  ${(props) => props.theme.fonts.noItem};
  text-align: center;
`
const ButtonsContainer = styled('div')`
  flex-direction: row;
  display: flex;
  flex-wrap: wrap;
  margin-bottom: 28px;
`
const AllImagesContainer = styled('div')`
  flex-direction: row;
  margin: 70px 50px 0px;
`
const ActionContainer = styled('div')<{ hide?: boolean }>`
  ${(props) => props.hide && 'display: none;'}
  margin-right: 30px;
  margin-bottom: 12px;
`
